import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageMeta from "../seo/PageMeta";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, where, Timestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";

const allowedEmails = [
  "iwona.bendig@egodziny.pl",
  "krzysztof.warda@egodziny.pl",
  "dominik.pogorzelski@egodziny.pl",
  "wiktor.spryszynski@egodziny.pl",
];

const CALCULATOR_PATH_PREFIX = "/kalkulator-oszczednosci/?user=";

const formatDateTime = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
  }
  if (value.toDate) {
    return value.toDate().toLocaleString("pl-PL");
  }
  return "-";
};

const extractUser = (path) => {
  if (!path) return "-";
  const value = path.slice(CALCULATOR_PATH_PREFIX.length);
  try {
    return decodeURIComponent(value) || "-";
  } catch {
    return value || "-";
  }
};

const PERIOD_OPTIONS = [
  { value: "7", label: "Ostatnie 7 dni" },
  { value: "14", label: "Ostatnie 14 dni" },
  { value: "30", label: "Ostatnie 30 dni" },
  { value: "90", label: "Ostatnie 90 dni" },
  { value: "all", label: "Wszystko" },
];

export default function AdminPanelCalculator() {
  const [pageViews, setPageViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [period, setPeriod] = useState("30");
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login/blog?redirect=/admin-panel/kalkulator");
        return;
      }
      if (!allowedEmails.includes(currentUser.email)) {
        alert("Brak dostępu");
        navigate("/");
        return;
      }
      setIsAuthorized(true);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const loadPageViews = async () => {
      setLoading(true);
      let q;
      if (period === "all") {
        q = query(collection(db, "page_views"), orderBy("createdAt", "desc"));
      } else {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - parseInt(period, 10));
        q = query(
          collection(db, "page_views"),
          where("createdAt", ">=", Timestamp.fromDate(daysAgo)),
          orderBy("createdAt", "desc")
        );
      }
      const snapshot = await getDocs(q);
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setPageViews(rows);
      setLoading(false);
    };

    if (isAuthorized) {
      loadPageViews();
    }
  }, [isAuthorized, period]);

  const entries = useMemo(
    () => pageViews.filter((view) => view.path && view.path.startsWith(CALCULATOR_PATH_PREFIX)),
    [pageViews]
  );

  return (
    <>
      <PageMeta path="/admin-panel/kalkulator" />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <Menu />
      <main style={{ padding: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <h1 style={{ margin: 0 }}>Wejścia kalkulator</h1>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <button className="button-default btn" onClick={() => navigate("/admin-panel")}>
              Formularze
            </button>
            <button className="button-default btn" onClick={() => navigate("/admin-panel/page-views")}>
              Statystyki wyświetleń
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              Okres:
              <select
                className="login-input"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                style={{ maxWidth: "200px" }}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <p>Ładowanie...</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Data</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>User</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Ścieżka</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Referrer</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatDateTime(entry.timestamp || entry.createdAt)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontWeight: 500 }}>
                      {extractUser(entry.path)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontFamily: "monospace", fontSize: "13px", color: "#666" }}>
                      {entry.path}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee", fontSize: "13px", color: "#666" }}>
                      {entry.referrer || "-"}
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: "10px" }}>Brak danych.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
